/**
 * 数据操作常用方法
 *
 * @format
 */

// 对象转JSON
import { isObject } from './is';
import { getRandom } from './uUuid';

/**
 *
 * byte to size
 * formatBytes(1024);       // 1 KB
 * formatBytes('1024');     // 1 KB
 * formatBytes(1234);       // 1.21 KB
 * formatBytes(1234, 3);    // 1.205 KB
 * @param {number} bytes file size
 */
export function formatSizeUnits(bytes, decimals = 2) {
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function deepMerge<T = any>(src: any = {}, target: any = {}): T {
	let key: string;
	for (key in target) {
		src[key] = isObject(src[key]) ? deepMerge(src[key], target[key]) : (src[key] = target[key]);
	}
	return src;
}

export const toJSON = (obj) => {
	return JSON.stringify(obj, (_, value) => {
		switch (true) {
			case typeof value === 'undefined':
				return 'undefined';
			case typeof value === 'symbol':
				return value.toString();
			case typeof value === 'function':
				return value.toString();
			default:
				break;
		}
		return value;
	});
};

/**
 *  @description 将一维数组转成树形结构数据
 * @param items
 * @param id
 * @param link
 */
export const generateTree = (items, id = 0, link = 'parent') => {
	return items
		.filter((item) => item[link] == id)
		.map((item) => ({
			...item,
			slots: { title: 'name' },
			children: generateTree(items, item.departmentid),
		}));
};

// 摊平数组
export const flatArr = (arr: any[], children = 'children'): any[] => {
	return arr.reduce((count, itm) => {
		if (itm[children]) {
			return [...count, itm, ...flatArr(itm[children], children)];
		} else {
			return [...count, itm];
		}
	}, []);
};

/**
 * 可根据属性对数组去重
 * @arr 数组
 * @type 去重属性，可选
 * @returns [0, 1] 0:去重后的数组，1: 重复的数据数组
 */
export const uniq = <T>(arr: T[], type?: string): [T[], T[]] =>
	arr.reduce(
		(count, itm) => {
			return count[0].find((item) => (type ? item[type] === itm[type] : item === itm))
				? [
						[...count[0], itm],
						[...count[1], itm],
				  ]
				: [[...count[0], itm], [...count[1]]];
		},
		[[], []],
	);
/**
 * 根据keys选择对象中的属性
 * @value 对象
 * @keys 选择的key，以`.`分隔
 * @returns 选择的对象
 */
export const chain = (value: Record<string, any>, keys: string) =>
	keys.split('.').reduce((val, item) => {
		return val[item] || '';
	}, value);

/**
 * @param {string} input value
 * @returns {number} output value
 */
export function byteLength(str) {
	// returns the byte length of an utf8 string
	let s = str.length;
	for (let i = str.length - 1; i >= 0; i--) {
		const code = str.charCodeAt(i);
		if (code > 0x7f && code <= 0x7ff) s++;
		else if (code > 0x7ff && code <= 0xffff) s += 2;
		if (code >= 0xdc00 && code <= 0xdfff) i--;
	}
	return s;
}

/**
 * @param {Array} actual
 * @returns {Array}
 */
export function cleanArray(actual) {
	const newArray: any = [];
	for (let i = 0; i < actual.length; i++) {
		if (actual[i]) {
			newArray.push(actual[i]);
		}
	}
	return newArray;
}

/**
 * Merges two objects, giving the last one precedence
 * @param {Object} target
 * @param {(Object|Array)} source
 * @returns {Object}
 */
export function objectMerge(target, source) {
	if (typeof target !== 'object') {
		target = {};
	}
	if (Array.isArray(source)) {
		return source.slice();
	}
	Object.keys(source).forEach((property) => {
		const sourceProperty = source[property];
		if (typeof sourceProperty === 'object') {
			target[property] = objectMerge(target[property], sourceProperty);
		} else {
			target[property] = sourceProperty;
		}
	});
	return target;
}

/**
 * This is just a simple version of deep copy
 * Has a lot of edge cases bug
 * If you want to use a perfect deep copy, use lodash's _.cloneDeep
 * @param {Object} source
 * @returns {Object}
 */
export function deepClone(source) {
	if (!source && typeof source !== 'object') {
		throw new Error('error arguments' + 'deepClone');
	}
	const targetObj = source.constructor === Array ? [] : {};
	Object.keys(source).forEach((keys) => {
		if (source[keys] && typeof source[keys] === 'object') {
			targetObj[keys] = deepClone(source[keys]);
		} else {
			targetObj[keys] = source[keys];
		}
	});
	return targetObj;
}

/**
 * @param {Array} arr
 * @returns {Array}
 */
export function uniqueArr(arr) {
	return Array.from(new Set(arr));
}

// 首字母大小
export function titleCase(str) {
	return str.replace(/( |^)[a-z]/g, (L) => L.toUpperCase());
}

// 下划转驼峰
export function camelCase(str) {
	return str.replace(/_[a-z]/g, (str1) => str1.substr(-1).toUpperCase());
}

export function makeMap(str, expectsLowerCase) {
	const map = Object.create(null);
	const list = str.split(',');
	for (let i = 0; i < list.length; i++) {
		map[list[i]] = true;
	}
	return expectsLowerCase ? (val) => map[val.toLowerCase()] : (val) => map[val];
}

/**
 * @returns {string}
 */
export function createUniqueString() {
	const timestamp = `${+new Date()}`;
	const randomNum = `${parseInt((1 + getRandom()) * 65536)}`;
	return (+(randomNum + timestamp)).toString(32);
}

// 转换字符串，undefined,null等转化为""
export function parseStrEmpty(str) {
	if (!str || str == 'undefined' || str == 'null') {
		return '';
	}
	return str;
}

// 数据合并
export function mergeRecursive(source, target) {
	for (const p in target) {
		try {
			if (target[p].constructor == Object) {
				source[p] = mergeRecursive(source[p], target[p]);
			} else {
				source[p] = target[p];
			}
		} catch (e) {
			source[p] = target[p];
		}
	}
	return source;
}

/**
 * 构造树型结构数据
 * @param {*} data 数据源
 * @param {*} id id字段 默认 'id'
 * @param {*} parentId 父节点字段 默认 'parentId'
 * @param {*} children 孩子节点字段 默认 'children'
 */
export function handleTree(data, id, parentId, children) {
	const config = {
		id: id || 'id',
		parentId: parentId || 'parentId',
		childrenList: children || 'children',
	};

	const childrenListMap = {};
	const nodeIds = {};
	const tree: any = [];

	for (const d of data) {
		const parentId = d[config.parentId];
		if (childrenListMap[parentId] == null) {
			childrenListMap[parentId] = [];
		}
		nodeIds[d[config.id]] = d;
		childrenListMap[parentId].push(d);
	}

	for (const d of data) {
		const parentId = d[config.parentId];
		if (nodeIds[parentId] == null) {
			tree.push(d);
		}
	}

	for (const t of tree) {
		adaptToChildrenList(t);
	}

	function adaptToChildrenList(o) {
		if (childrenListMap[o[config.id]] !== null) {
			o[config.childrenList] = childrenListMap[o[config.id]];
		}
		if (o[config.childrenList]) {
			for (const c of o[config.childrenList]) {
				adaptToChildrenList(c);
			}
		}
	}
	return tree;
}

/**
 * 转化 base64 字符串为 blob 格式
 * 上传文件到服务器前可能会调用这个方法
 * @param urlData base64URL 可以转化成图片的 base64 字符串
 * @returns blob 要被上传到服务器的图片文件
 */
export function convertBase64UrlToBlob(urlData) {
	const bytes = window.atob(urlData.split(',')[1]); //去掉url的头，并转换为byte
	//处理异常,将ascii码小于0的转换为大于0
	const ab = new ArrayBuffer(bytes.length);
	const ia = new Uint8Array(ab);
	for (let i = 0; i < bytes.length; i++) {
		ia[i] = bytes.charCodeAt(i);
	}

	return new Blob([ab], {
		type: 'image/png',
	});
}

// 字符串格式化(%s )
export function sprintf(str, ...args) {
	let flag = true,
		i = 1;
	str = str.replace(/%s/g, function () {
		const arg = args[i++];
		if (typeof arg === 'undefined') {
			flag = false;
			return '';
		}
		return arg;
	});
	return flag ? str : '';
}
